import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Check, Palette } from "lucide-react";
import { useEffect } from "react";
import {
  useTheme,
  themeOptions,
  useThemeEffect,
  ThemeOption,
} from "@/hooks/use-theme";
import { EnhancedHeader } from "@/components/ui";

export default function ThemesPage() {
  const [_, setLocation] = useLocation();
  const { theme, setTheme } = useTheme();

  // Apply theme when this component mounts
  useThemeEffect();

  return (
    <div className="min-h-screen bg-black text-white">
      <EnhancedHeader
        title="Game Themes"
        backPath="/user-settings"
        coloredText={true}
        rightContent={null}
        showBackButton={true}
      />

      <div className="max-w-md mx-auto px-4 pb-10">
        <div className="bg-zinc-900 rounded-xl overflow-hidden mb-4">
          <h3 className="text-xs uppercase text-gray-400 font-medium px-4 pt-3 pb-1">
            Select Game Theme
          </h3>
          <p className="text-xs text-gray-400 px-4 pb-2">
            Choose a theme to customize your gaming experience
          </p>
          
          <div className="p-4 pt-2 grid grid-cols-1 gap-4">
            {Object.entries(themeOptions).map(([key, t]) => (
              <ThemeCard
                key={key}
                themeKey={key as ThemeOption}
                theme={t}
                isActive={theme === key}
                onSelect={() => setTheme(key as ThemeOption)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ThemeCardProps {
  themeKey: ThemeOption;
  theme: (typeof themeOptions)[ThemeOption];
  isActive: boolean;
  onSelect: () => void;
}

function ThemeCard({ themeKey, theme, isActive, onSelect }: ThemeCardProps) {
  return (
    <div
      className={`relative overflow-hidden rounded-lg cursor-pointer transition-all duration-200 ${
        isActive 
          ? "ring-2 ring-green-500 bg-zinc-800" 
          : "bg-zinc-800/50 hover:bg-zinc-800"
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center p-3">
        {/* Theme Preview */}
        <div className="mr-3 h-14 w-14 rounded-md overflow-hidden flex-shrink-0">
          <div
            className="w-full h-full"
            style={{
              background: `linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.primary} 100%)`,
            }}
          />
        </div>

        {/* Theme Info */}
        <div className="flex-1">
          <h3 className="text-base font-medium">{theme.name}</h3>
          <div className="flex gap-2 mt-1">
            <div
              className="w-5 h-5 rounded-full"
              style={{ backgroundColor: theme.colors.primary }}
              title="Primary color"
            ></div>
            <div
              className="w-5 h-5 rounded-full"
              style={{ backgroundColor: theme.colors.accent }}
              title="Accent color"
            ></div>
            <div
              className="w-5 h-5 rounded-full border border-zinc-700"
              style={{ backgroundColor: theme.colors.background }}
              title="Background color"
            ></div>
          </div>
        </div>

        {isActive && (
          <div className="bg-green-600 rounded-full p-1 flex-shrink-0">
            <Check className="h-4 w-4 text-white" />
          </div>
        )}
      </div>
    </div>
  );
}
